import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createBlobUrl, revokeBlobUrl, triggerDownload } from '@components/PdfViewer/utils';
import { useEventTracking } from '@services/statsig';
import type {
  CaptureChargesRequest,
  RecordPaymentRequest,
  WriteOffRequest,
} from '@store/generated/billing';
import type {
  BatchSubmitClaimsProgressResponse,
  BatchSubmitClaimsResponse,
  ClaimScrubRequest,
  CreateClaimRequest,
  SubmitClaimResponse,
} from '@store/generated/claims';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { useSnackbar } from 'notistack';

import type { ClaimUpdateRequest, SubmitClaimRequest } from '../types';
import { createIdempotencyKey } from '../utils';

import {
  billingApi,
  useBatchSubmitClaimsMutation,
  useCaptureChargesMutation,
  useClaimAttentionMutation,
  useClaimRebuildMutation,
  useClaimScrubMutation,
  useCreateClaimMutation,
  useCreateSecondaryClaimMutation,
  useDeleteClaimMutation,
  useDownloadClaimsX12BulkMutation,
  useDownloadRemittanceErasBulkMutation,
  useGetBatchSubmitProgressQuery,
  useGetBillingClaimsListQuery,
  useGetBillingEncountersQuery,
  useGetClaimByIdQuery,
  useGetClaimPdfQuery,
  useGetClaimQueueCountsQuery,
  useGetClaimsQuery,
  useGetEncounterDetailQuery,
  useGetEncounterFlowQuery,
  useGetProviderRemittancesQuery,
  useGetRemittanceQueueCountsQuery,
  useGetStatementsHistoryQuery,
  useGetStatementsQueueQuery,
  useGetUnifiedEncounterBillingQuery,
  useLazyDownloadClaimX12Query,
  useLazyDownloadRemittanceEraQuery,
  useRecordPaymentMutation,
  useResubmitClaimMutation,
  useRemoveRecordedPaymentMutation,
  useReverseChargeLineMutation,
  useSubmitClaimIdempotentMutation,
  useUpdateClaimMutation,
  useWriteOffMutation,
} from './api';
import {
  selectScrubberNavigation,
  selectScrubberPanel,
  selectScrubberPreferences,
} from './selectors';
import {
  clearHighlight,
  closePanel,
  navigateToField,
  openPanel,
  setPanelPosition,
  setPanelWidth,
  togglePanel,
  togglePanelCollapse,
} from './slice';

/**
 * Billing URL prefix used for all JotBill navigation. Kept as a hook so
 * callers don't need to be updated if the prefix ever changes again.
 */
export function useBillingBasePath(): '/jotbill' {
  return '/jotbill';
}

/**
 * @deprecated Use `useBillingClaimsList` instead — it uses the new org-scoped enriched endpoint.
 */
export function useBillingClaims(
  filters?: {
    patientId?: string;
    status?: string;
    dateFrom?: string;
    dateTo?: string;
    page?: number;
    pageSize?: number;
    sortBy?: 'created_at' | 'service_date_from' | 'claim_number' | 'total_charge_cents';
    sortOrder?: 'asc' | 'desc';
  },
  pollingInterval?: number,
) {
  // Build query params, excluding null/undefined values
  const queryParams = useMemo(() => {
    const params: Record<string, string | number> = {
      page: filters?.page ?? 1,
      pageSize: filters?.pageSize ?? 20,
      sortBy: filters?.sortBy ?? 'created_at',
      sortOrder: filters?.sortOrder ?? 'desc',
    };

    // Only add optional params if they have values
    if (filters?.patientId) params.patientId = filters.patientId;
    if (filters?.status) params.status = filters.status;
    if (filters?.dateFrom) params.dateFrom = filters.dateFrom;
    if (filters?.dateTo) params.dateTo = filters.dateTo;

    return params;
  }, [filters]);

  const { data, isLoading, isFetching, error, refetch } = useGetClaimsQuery(queryParams, {
    pollingInterval,
  });

  return {
    claims: data?.claims ?? [],
    totalCount: data?.total ?? 0,
    currentPage: filters?.page ?? 1,
    totalPages: Math.ceil((data?.total ?? 0) / (filters?.pageSize ?? 20)),
    isLoading,
    isFetching,
    error,
    refetch,
  };
}

/**
 * Hook for the new org-scoped enriched claims list.
 * Returns ClaimListItem[] with computed intelligence fields (next_action, readiness, annotation counts).
 */
export function useBillingClaimsList(
  filters?: {
    patientId?: string;
    providerIds?: string;
    status?: string;
    insurancePayerId?: string;
    dateFrom?: string;
    dateTo?: string;
    page?: number;
    perPage?: number;
    sortBy?: 'created_at' | 'service_date_from' | 'total_charge_cents';
    sortOrder?: 'asc' | 'desc';
    includeSuperseded?: boolean;
  },
  pollingInterval?: number,
) {
  const queryParams = useMemo(() => {
    const params: Record<string, string | number | boolean> = {
      page: filters?.page ?? 1,
      perPage: filters?.perPage ?? 20,
      sortBy: filters?.sortBy ?? 'created_at',
      sortOrder: filters?.sortOrder ?? 'desc',
    };

    if (filters?.patientId) params.patientId = filters.patientId;
    if (filters?.providerIds) params.providerIds = filters.providerIds;
    if (filters?.status) params.status = filters.status;
    if (filters?.insurancePayerId) params.insurancePayerId = filters.insurancePayerId;
    if (filters?.dateFrom) params.dateFrom = filters.dateFrom;
    if (filters?.dateTo) params.dateTo = filters.dateTo;
    if (filters?.includeSuperseded) params.includeSuperseded = true;

    return params;
  }, [filters]);

  const { data, isLoading, isFetching, error, refetch } = useGetBillingClaimsListQuery(queryParams, {
    pollingInterval,
  });

  return {
    claims: data?.claims ?? [],
    totalCount: data?.total ?? 0,
    currentPage: data?.page ?? 1,
    totalPages: data?.total_pages ?? 1,
    perPage: data?.per_page ?? 20,
    isLoading,
    isFetching,
    error,
    refetch,
  };
}

/**
 * Hook for claim queue badge counts (needs_attention, ready_to_submit, etc.).
 * Accepts the same filters as the claims list (except status) to keep counts in sync.
 */
export function useClaimQueueCounts(filters?: {
  patientId?: string;
  providerIds?: string;
  insurancePayerId?: string;
  dateFrom?: string;
  dateTo?: string;
  includeSuperseded?: boolean;
}) {
  const queryParams = useMemo(() => {
    const params: Record<string, string | boolean> = {};
    if (filters?.patientId) params.patientId = filters.patientId;
    if (filters?.providerIds) params.providerIds = filters.providerIds;
    if (filters?.insurancePayerId) params.insurancePayerId = filters.insurancePayerId;
    if (filters?.dateFrom) params.dateFrom = filters.dateFrom;
    if (filters?.dateTo) params.dateTo = filters.dateTo;
    if (filters?.includeSuperseded) params.includeSuperseded = true;
    return params;
  }, [filters]);

  const { data, isLoading, isFetching, error, refetch } = useGetClaimQueueCountsQuery(queryParams);

  return {
    counts: data ?? null,
    isLoading,
    isFetching,
    error,
    refetch,
  };
}

/**
 * Hook for managing billing encounters (org-scoped)
 */
export function useBillingEncounters(filters?: {
  providerIds?: string;
  patientId?: string;
  payerUuid?: string;
  status?: string;
  billable?: boolean;
  hasClaim?: boolean;
  readyToBill?: string;
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  perPage?: number;
  sortBy?: 'service_date' | 'created_at';
  sortOrder?: 'asc' | 'desc';
}) {
  const queryParams = useMemo(() => {
    const params: Record<string, string | number | boolean> = {
      page: filters?.page ?? 1,
      perPage: filters?.perPage ?? 20,
      // Behaviour-identical to the 'scheduled_for' this replaced: that
      // key is aliased to service_date server-side, so both resolve to
      // the same column today. Sending the canonical one removes the
      // last dependency on that alias (gdn-3104).
      sortBy: filters?.sortBy ?? 'service_date',
      sortOrder: filters?.sortOrder ?? 'desc',
    };

    if (filters?.providerIds) params.providerIds = filters.providerIds;
    if (filters?.patientId) params.patientId = filters.patientId;
    if (filters?.status) params.status = filters.status;
    if (filters?.billable !== undefined) params.billable = filters.billable;
    if (filters?.hasClaim !== undefined) params.hasClaim = filters.hasClaim;
    if (filters?.readyToBill) params.readyToBill = filters.readyToBill;
    if (filters?.payerUuid) params.payerUuid = filters.payerUuid;
    if (filters?.dateFrom) params.dateFrom = filters.dateFrom;
    if (filters?.dateTo) params.dateTo = filters.dateTo;

    return params;
  }, [filters]);

  const { data, isLoading, isFetching, error, refetch } = useGetBillingEncountersQuery(queryParams);

  return {
    encounters: data?.encounters ?? [],
    totalCount: data?.total ?? 0,
    currentPage: data?.page ?? 1,
    totalPages: data?.total_pages ?? 1,
    perPage: data?.per_page ?? 20,
    isLoading,
    isFetching,
    error,
    refetch,
  };
}

/**
 * Hook for the remittances list with enriched data, filters, sorting, and queue support.
 * Mirrors the useBillingClaimsList pattern.
 */
export function useRemittancesList(filters?: {
  dateFrom?: string;
  dateTo?: string;
  payerName?: string;
  paymentMethod?: string;
  queue?: string;
  page?: number;
  perPage?: number;
  sortBy?: 'payment_date' | 'total_payment_amount_cents' | 'created_at' | 'payer_name';
  sortOrder?: 'asc' | 'desc';
}) {
  const queryParams = useMemo(() => {
    const params: Record<string, string | number> = {
      page: filters?.page ?? 1,
      perPage: filters?.perPage ?? 25,
    };

    if (filters?.sortBy) params.sortBy = filters.sortBy;
    if (filters?.sortOrder) params.sortOrder = filters.sortOrder;
    if (filters?.dateFrom) params.dateFrom = filters.dateFrom;
    if (filters?.dateTo) params.dateTo = filters.dateTo;
    if (filters?.payerName) params.payerName = filters.payerName;
    if (filters?.paymentMethod) params.paymentMethod = filters.paymentMethod;
    if (filters?.queue) params.queue = filters.queue;

    return params;
  }, [filters]);

  const { data, isLoading, isFetching, error, refetch } = useGetProviderRemittancesQuery(queryParams);

  return {
    remittances: data?.remittances ?? [],
    totalCount: data?.total ?? 0,
    currentPage: data?.page ?? 1,
    totalPages: data?.total_pages ?? 1,
    perPage: data?.per_page ?? 25,
    isLoading,
    isFetching,
    error,
    refetch,
  };
}

/**
 * Hook for remittance queue badge counts (all, has_denials, reversals, clean, imported).
 * Accepts the same filters as the remittances list (except queue) to keep counts in sync.
 */
export function useRemittanceQueueCounts(filters?: {
  dateFrom?: string;
  dateTo?: string;
  payerName?: string;
  paymentMethod?: string;
}) {
  const queryParams = useMemo(() => {
    const params: Record<string, string> = {};
    if (filters?.dateFrom) params.dateFrom = filters.dateFrom;
    if (filters?.dateTo) params.dateTo = filters.dateTo;
    if (filters?.payerName) params.payerName = filters.payerName;
    if (filters?.paymentMethod) params.paymentMethod = filters.paymentMethod;
    return params;
  }, [filters]);

  const { data, isLoading, isFetching, error, refetch } = useGetRemittanceQueueCountsQuery(queryParams);

  return {
    counts: data ?? null,
    isLoading,
    isFetching,
    error,
    refetch,
  };
}

/**
 * Hook for the patient statements queue — patients with confirmed balances ready for billing.
 */
export function useStatementsQueue(filters?: {
  page?: number;
  perPage?: number;
}) {
  const queryParams = useMemo(() => {
    const params: Record<string, number> = {
      page: filters?.page ?? 1,
      perPage: filters?.perPage ?? 20,
    };
    return params;
  }, [filters]);

  const { data, isLoading, isFetching, error, refetch } = useGetStatementsQueueQuery(queryParams);

  return {
    items: data?.items ?? [],
    totalCount: data?.total ?? 0,
    currentPage: data?.page ?? 1,
    totalPages: data?.total_pages ?? 1,
    perPage: data?.per_page ?? 20,
    isLoading,
    isFetching,
    error,
    refetch,
  };
}

/**
 * Hook for statement history — completed and failed statement runs.
 */
export function useStatementsHistory(filters?: {
  page?: number;
  perPage?: number;
  triggerType?: string;
  dateFrom?: string;
  dateTo?: string;
}) {
  const queryParams = useMemo(() => {
    const params: Record<string, string | number> = {
      page: filters?.page ?? 1,
      perPage: filters?.perPage ?? 20,
    };
    if (filters?.triggerType) params.triggerType = filters.triggerType;
    if (filters?.dateFrom) params.dateFrom = filters.dateFrom;
    if (filters?.dateTo) params.dateTo = filters.dateTo;
    return params;
  }, [filters]);

  const { data, isLoading, isFetching, error, refetch } = useGetStatementsHistoryQuery(queryParams);

  return {
    items: data?.items ?? [],
    totalCount: data?.total ?? 0,
    currentPage: data?.page ?? 1,
    totalPages: data?.total_pages ?? 1,
    perPage: data?.per_page ?? 20,
    isLoading,
    isFetching,
    error,
    refetch,
  };
}

/**
 * Hook for fetching a single encounter detail
 */
export function useEncounterDetail(encounterId?: string) {
  const { data, isLoading, isFetching, error, refetch } = useGetEncounterDetailQuery(
    { encounterId: encounterId ?? '' },
    { skip: !encounterId },
  );

  return {
    encounter: data ?? null,
    isLoading,
    isFetching,
    error,
    refetch,
  };
}

/**
 * Hook for fetching the unified 5S flow for a single encounter
 */
export function useEncounterFlow(encounterId?: string) {
  const { data, isLoading, isFetching, error, refetch } = useGetEncounterFlowQuery(
    { encounterId: encounterId ?? '' },
    { skip: !encounterId },
  );

  return {
    flow: data ?? null,
    isLoading,
    isFetching,
    error,
    refetch,
  };
}

/**
 * Hook for fetching the unified encounter billing read model.
 */
export function useUnifiedEncounterBilling(encounterId?: string) {
  const { data, isLoading, isFetching, error, refetch } = useGetUnifiedEncounterBillingQuery(
    { encounterId: encounterId ?? '' },
    { skip: !encounterId },
  );

  return {
    unifiedEncounter: data ?? null,
    isLoading,
    isFetching,
    error,
    refetch,
  };
}

/**
 * Hook for claim submission and management
 */
export function useClaimSubmission() {
  const [createClaim, { isLoading: isCreating }] = useCreateClaimMutation();
  const [updateClaim, { isLoading: isUpdating }] = useUpdateClaimMutation();
  const [deleteClaim, { isLoading: isDeleting }] = useDeleteClaimMutation();

  const submitClaim = useCallback(
    async (claimData: CreateClaimRequest) => {
      try {
        const result = await createClaim({ createClaimRequest: claimData }).unwrap();
        return { success: true, data: result };
      } catch (error) {
        console.error('Failed to submit claim:', error);
        return { success: false, error };
      }
    },
    [createClaim],
  );

  const updateExistingClaim = useCallback(
    async (claimId: string, updates: ClaimUpdateRequest) => {
      try {
        const result = await updateClaim({
          claimId,
          updateClaimRequest: updates,
        }).unwrap();
        return { success: true, data: result };
      } catch (error) {
        console.error('Failed to update claim:', error);
        return { success: false, error };
      }
    },
    [updateClaim],
  );

  const deleteExistingClaim = useCallback(
    async (claimId: string) => {
      try {
        await deleteClaim({ claimId }).unwrap();
        return { success: true };
      } catch (error) {
        console.error('Failed to delete claim:', error);
        return { success: false, error };
      }
    },
    [deleteClaim],
  );

  return {
    submitClaim,
    updateClaim: updateExistingClaim,
    deleteClaim: deleteExistingClaim,
    isSubmitting: isCreating || isUpdating || isDeleting,
  };
}

/**
 * Hook for getting a single claim by ID
 */
export function useClaimDetails(claimId?: string) {
  const { data, isLoading, error, refetch } = useGetClaimByIdQuery(
    { claimId: claimId ?? '' },
    { skip: !claimId },
  );

  return {
    claim: data,
    isLoading,
    error,
    refetch,
  };
}

/**
 * Hook for generating claim PDF
 */
export function useClaimPdf(claimId?: string) {
  const { data, isLoading, error, refetch } = useGetClaimPdfQuery(
    { claimId: claimId ?? '' },
    { skip: !claimId },
  );

  const downloadPdf = useCallback(() => {
    if (!data?.pdf_data) return;

    // Convert base64 to blob and open in new window
    const byteCharacters = atob(data.pdf_data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
  }, [data?.pdf_data]);

  return {
    pdfData: data?.pdf_data,
    filename: data?.filename,
    isGenerating: isLoading,
    error,
    downloadPdf,
    regenerate: refetch,
  };
}

/**
 * Hook for scrubbing/validating a claim
 */
export function useClaimScrub() {
  const [scrubClaim, { isLoading: isScrubbing }] = useClaimScrubMutation();

  const performScrub = useCallback(
    async (claimId: string, scrubData?: ClaimScrubRequest) => {
      try {
        const result = await scrubClaim({
          claimId,
          claimScrubRequest: scrubData || {},
        }).unwrap();
        return { success: true, data: result };
      } catch (error) {
        console.error('Failed to scrub claim:', error);
        return { success: false, error };
      }
    },
    [scrubClaim],
  );

  return {
    scrubClaim: performScrub,
    isScrubbing,
  };
}

/**
 * Hook for submitting a claim to clearinghouse.
 *
 * Dedup strategy (GDN-1051):
 * 1. A per-claim in-flight promise cache — a second call for the same claim
 *    while the first is still running returns the same promise instead of
 *    dispatching a second mutation. React's `isSubmitting` state update lags
 *    behind a rapid second click, so a ref-based guard is necessary. Keying
 *    by claim_id (rather than a single boolean) keeps legitimate concurrent
 *    submissions for *different* claims working — e.g. batch "Submit selected"
 *    flows that fire Promise.allSettled across N claims.
 * 2. A fresh Idempotency-Key header accompanies every submission. If the
 *    first request actually reaches the server and a retry fires (flaky
 *    connection, StrictMode double-invoke, cross-tab), the server replays
 *    the cached response instead of submitting a second time to Stedi.
 *
 * The idempotency key is generated per user-initiated click and discarded on
 * completion — never reused across distinct submit intents, since that would
 * silently swallow a legitimate follow-up attempt.
 */
export function useClaimSubmit() {
  const [submitClaimMutation, { isLoading: isSubmitting }] = useSubmitClaimIdempotentMutation();
  const { openPanel } = useClaimScrubberUI();
  const inFlightRef = useRef(new Map<string, Promise<ClaimSubmitResult>>());

  const performSubmit = useCallback(
    (claimId: string, submitData?: SubmitClaimRequest): Promise<ClaimSubmitResult> => {
      const existing = inFlightRef.current.get(claimId);
      if (existing !== undefined) {
        // Rapid second click for the same claim — reuse the in-flight promise
        // so the caller gets a consistent result without a second mutation.
        return existing;
      }

      const promise = submitClaimMutation({
        claimId,
        submitClaimRequest: submitData || {},
        idempotencyKey: createIdempotencyKey(),
      })
        .unwrap()
        .then(result => {
          if (result?.submission_blocked && result?.claim?.annotations?.length) {
            openPanel();
          }
          return {
            success: true,
            data: result,
            submissionBlocked: result?.submission_blocked || false,
          };
        })
        .catch((error: unknown) => {
          console.error('Failed to submit claim:', error);
          return { success: false, error, submissionBlocked: false };
        })
        .finally(() => {
          inFlightRef.current.delete(claimId);
        });

      inFlightRef.current.set(claimId, promise);
      return promise;
    },
    [submitClaimMutation, openPanel],
  );

  return {
    submitClaim: performSubmit,
    isSubmitting,
  };
}

interface ClaimSubmitResult {
  success: boolean;
  data?: SubmitClaimResponse;
  error?: unknown;
  submissionBlocked: boolean;
}

const ACTIVE_CLAIM_BATCH_STORAGE_KEY = 'jotbill:active-claim-submit-batch';
const TRACKED_CLAIM_BATCH_STORAGE_PREFIX = 'jotbill:tracked-claim-submit-batch:';
const TERMINAL_CLAIM_BATCH_STATUSES: Array<BatchSubmitClaimsProgressResponse['status']> = [
  'completed',
  'completed_with_failures',
  'failed',
];

/**
 * Hook wrapping batch claim submission with idempotency + in-flight dedupe.
 *
 * The underlying `batchSubmit` endpoint dispatches a Celery task per call, so
 * a double-clicked "Submit All" button would otherwise enqueue two identical
 * batches and submit every claim twice. This wrapper:
 *   1. Reuses an in-flight promise keyed by the sorted claim_ids, so a rapid
 *      second click returns the first request's promise instead of firing a
 *      second mutation.
 *   2. Sends an `Idempotency-Key` header; the server replays the cached
 *      response so per-claim submits dedupe even if two identical dispatches
 *      slip past the client guard.
 */
export function useBatchClaimSubmit() {
  const dispatch = useAppDispatch();
  const [batchSubmit, { isLoading: isBatchSubmitting }] = useBatchSubmitClaimsMutation();
  const { billing } = useEventTracking();
  const [activeBatchId, setActiveBatchId] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    return window.sessionStorage.getItem(ACTIVE_CLAIM_BATCH_STORAGE_KEY);
  });
  const [pollBatch, setPollBatch] = useState(true);
  const inFlightRef = useRef(new Map<string, Promise<BatchSubmitClaimsResponse>>());
  const {
    data: batchProgress,
    error: batchProgressError,
    isFetching: isBatchProgressFetching,
  } = useGetBatchSubmitProgressQuery(
    { batchId: activeBatchId ?? '' },
    {
      pollingInterval: activeBatchId && pollBatch ? 750 : 0,
      refetchOnFocus: true,
      refetchOnReconnect: true,
      skip: !activeBatchId,
    },
  );

  const dismissBatch = useCallback(() => {
    setActiveBatchId(null);
    setPollBatch(true);
    if (typeof window !== 'undefined') {
      window.sessionStorage.removeItem(ACTIVE_CLAIM_BATCH_STORAGE_KEY);
    }
  }, []);

  useEffect(() => {
    if (!activeBatchId || !batchProgress) return;
    if (!TERMINAL_CLAIM_BATCH_STATUSES.includes(batchProgress.status)) return;
    setPollBatch(false);
    if (typeof window === 'undefined') return;
    const trackingKey = `${TRACKED_CLAIM_BATCH_STORAGE_PREFIX}${activeBatchId}`;
    if (window.sessionStorage.getItem(trackingKey)) return;
    window.sessionStorage.setItem(trackingKey, 'true');
    dispatch(billingApi.util.invalidateTags([{ type: 'BillingV2ClaimWork' }]));
    for (const item of batchProgress.items ?? []) {
      if (
        !item.status ||
        !['submitted', 'blocked', 'failed', 'outcome_unknown'].includes(item.status)
      ) continue;
      billing.submitClaim(item.claim_id, {
        claim_status:
          item.status === 'submitted'
            ? 'submitted'
            : item.status === 'blocked'
              ? 'blocked'
              : 'failed',
        submission_method: 'batch',
      });
    }
  }, [activeBatchId, batchProgress, billing, dispatch]);

  useEffect(() => {
    if (!activeBatchId || !batchProgressError || typeof batchProgressError !== 'object') return;
    if ('status' in batchProgressError && batchProgressError.status === 404) dismissBatch();
  }, [activeBatchId, batchProgressError, dismissBatch]);

  const submitBatch = useCallback(
    (claimIds: string[]): Promise<BatchSubmitClaimsResponse> => {
      const dedupeKey = [...claimIds].sort().join(',');
      const existing = inFlightRef.current.get(dedupeKey);
      if (existing !== undefined) return existing;

      const promise = batchSubmit({
        batchSubmitClaimsRequest: { claim_ids: claimIds },
        idempotencyKey: createIdempotencyKey(),
      })
        .unwrap()
        .then(result => {
          setPollBatch(true);
          setActiveBatchId(result.batch_id);
          if (typeof window !== 'undefined') {
            window.sessionStorage.setItem(ACTIVE_CLAIM_BATCH_STORAGE_KEY, result.batch_id);
          }
          return result;
        })
        .finally(() => {
          inFlightRef.current.delete(dedupeKey);
        });
      inFlightRef.current.set(dedupeKey, promise);
      return promise;
    },
    [batchSubmit],
  );

  const visibleBatchProgress =
    activeBatchId && batchProgress?.batch_id === activeBatchId ? batchProgress : undefined;
  const isBatchTerminal = Boolean(
    visibleBatchProgress?.status &&
    TERMINAL_CLAIM_BATCH_STATUSES.includes(visibleBatchProgress.status),
  );

  return {
    activeBatchId,
    batchProgress: visibleBatchProgress,
    batchProgressError,
    dismissBatch,
    isBatchActive: Boolean(activeBatchId && !isBatchTerminal),
    isBatchProgressFetching,
    isBatchSubmitting,
    isBatchTerminal,
    submitBatch,
  };
}

/**
 * Hook for synchronously rebuilding a claim from current source data.
 */
export function useClaimRebuild() {
  const [rebuildClaimMutation, { isLoading: isRebuilding }] = useClaimRebuildMutation();

  const rebuildClaim = useCallback(
    async (claimId: string) => {
      try {
        const result = await rebuildClaimMutation({
          claimId,
          rebuildClaimRequest: {},
        }).unwrap();
        return { success: true, data: result };
      } catch (error) {
        console.error('Failed to rebuild claim:', error);
        return { success: false, error };
      }
    },
    [rebuildClaimMutation],
  );

  return {
    rebuildClaim,
    isRebuilding,
  };
}

/**
 * Hook for creating correction (code 7) or void (code 8) claims.
 * Creates a new draft claim derived from an existing submitted claim.
 */
export function useResubmitClaim() {
  const [resubmitMutation, { isLoading: isResubmitting }] = useResubmitClaimMutation();

  const resubmitClaim = useCallback(
    async (claimId: string, code: '7' | '8', originalRefNumber?: string) => {
      try {
        const result = await resubmitMutation({
          claimId,
          createResubmissionClaimRequest: {
            resubmission_code: code,
            original_reference_number: originalRefNumber || undefined,
          },
        }).unwrap();
        return { success: true, data: result };
      } catch (error) {
        console.error('Failed to resubmit claim:', error);
        return { success: false, error };
      }
    },
    [resubmitMutation],
  );

  return {
    resubmitClaim,
    isResubmitting,
  };
}

export function useCreateSecondary() {
  const [createSecondaryMutation, { isLoading: isCreatingSecondary }] =
    useCreateSecondaryClaimMutation();

  const createSecondaryClaim = useCallback(
    async (claimId: string) => {
      try {
        const result = await createSecondaryMutation({ claimId }).unwrap();
        return { success: true as const, data: result };
      } catch (error) {
        console.error('Failed to create secondary claim:', error);
        return { success: false as const, error };
      }
    },
    [createSecondaryMutation],
  );

  return {
    createSecondaryClaim,
    isCreatingSecondary,
  };
}

/**
 * Hook for dismissing or snoozing a claim's ERA triage attention flag.
 *
 * `dismiss` removes the claim from the needs-attention lane until a new ERA arrives.
 * `remindTomorrow` snoozes the claim until 9am local time tomorrow.
 */
export function useClaimAttention() {
  const [attentionMutation, { isLoading: isUpdating }] = useClaimAttentionMutation();

  const dismiss = useCallback(
    async (claimId: string) => {
      try {
        const result = await attentionMutation({
          claimId,
          claimAttentionRequest: { action: 'dismiss' },
        }).unwrap();
        return { success: true, data: result };
      } catch (error) {
        console.error('Failed to dismiss claim attention:', error);
        return { success: false, error };
      }
    },
    [attentionMutation],
  );

  const remindTomorrow = useCallback(
    async (claimId: string) => {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(9, 0, 0, 0);
      try {
        const result = await attentionMutation({
          claimId,
          claimAttentionRequest: {
            action: 'snooze',
            snooze_until: tomorrow.toISOString(),
          },
        }).unwrap();
        return { success: true, data: result };
      } catch (error) {
        console.error('Failed to snooze claim attention:', error);
        return { success: false, error };
      }
    },
    [attentionMutation],
  );

  return { dismiss, remindTomorrow, isUpdating };
}

/**
 * Combined hook for all billing data
 */
export function useBillingData(filters?: {
  patientId?: string;
  dateFrom?: string;
  dateTo?: string;
}) {
  const claims = useBillingClaims(filters);
  const encounters = useBillingEncounters({
    ...filters,
    status: 'pending',
  });

  const stats = useMemo(
    () => ({
      totalClaims: claims.totalCount,
      unbilledEncounters: encounters.totalCount,
      draftClaims: claims.claims.filter(c => c.status === 'draft').length,
      submittedClaims: claims.claims.filter(c => ['submitted', 'acknowledged', 'accepted', 'rejected'].includes(c.status ?? '')).length,
    }),
    [claims.totalCount, claims.claims, encounters.totalCount],
  );

  return {
    claims: claims.claims,
    encounters: encounters.encounters,
    stats,
    isLoading: claims.isLoading || encounters.isLoading,
    refetch: () => {
      claims.refetch();
      encounters.refetch();
    },
  };
}

// ============================================================================
// Patient Payment Hooks
// ============================================================================

/**
 * Hook for recording a manual payment (cash, check, external)
 */
export function useRecordPayment() {
  const [recordPaymentMutation, { isLoading, error }] = useRecordPaymentMutation();

  const recordPayment = useCallback(
    async (patientId: string, payment: RecordPaymentRequest) => {
      try {
        const result = await recordPaymentMutation({
          patientId,
          recordPaymentRequest: payment,
        }).unwrap();
        return { success: true, data: result };
      } catch (error) {
        console.error('Failed to record payment:', error);
        return { success: false, error };
      }
    },
    [recordPaymentMutation],
  );

  return { recordPayment, isLoading, error };
}

/**
 * Hook for capturing charges (self-pay or any patient billing)
 */
export function useCaptureCharges() {
  const [captureChargesMutation, { isLoading, error }] = useCaptureChargesMutation();

  const captureCharges = useCallback(
    async (charges: CaptureChargesRequest) => {
      try {
        const result = await captureChargesMutation({
          captureChargesRequest: charges,
        }).unwrap();
        return { success: true, data: result };
      } catch (error) {
        console.error('Failed to capture charges:', error);
        return { success: false, error };
      }
    },
    [captureChargesMutation],
  );

  return { captureCharges, isLoading, error };
}

/**
 * Hook for writing off patient charges
 */
export function useWriteOff() {
  const [writeOffMutation, { isLoading, error }] = useWriteOffMutation();

  const writeOff = useCallback(
    async (patientId: string, request: WriteOffRequest) => {
      try {
        const result = await writeOffMutation({
          patientId,
          writeOffRequest: request,
        }).unwrap();
        return { success: true, data: result };
      } catch (error) {
        console.error('Failed to record write-off:', error);
        return { success: false, error };
      }
    },
    [writeOffMutation],
  );

  return { writeOff, isLoading, error };
}


/**
 * Remove a payment that was recorded by mistake (gdn-3255).
 *
 * NOT a refund. The money never arrived — a biller typed it in error —
 * so this deletes a bookkeeping entry rather than moving funds. Nothing
 * in this flow's copy may imply otherwise; see RemoveRecordedPaymentDialog.
 *
 * The server is the eligibility authority and refuses with biller-readable
 * sentences (a Stripe-sourced payment must be refunded, an allocated one
 * un-applied first). Those are rendered verbatim.
 */
export function useRemoveRecordedPayment() {
  const [removeMutation, { isLoading, error }] = useRemoveRecordedPaymentMutation();

  const removeRecordedPayment = useCallback(
    async (paymentEntryId: string, note?: string) => {
      try {
        const result = await removeMutation({
          paymentEntryId,
          removeRecordedPaymentRequest: { note: note ?? null },
        }).unwrap();
        return { success: true as const, data: result };
      } catch (error) {
        console.error('Failed to remove recorded payment:', error);
        return { success: false as const, error };
      }
    },
    [removeMutation],
  );

  return { removeRecordedPayment, isLoading, error };
}

export function useReverseChargeLine() {
  const [reverseMutation, { isLoading, error }] = useReverseChargeLineMutation();

  const reverseChargeLine = useCallback(
    async (chargeLineId: string, note?: string) => {
      try {
        const result = await reverseMutation({
          chargeLineId,
          reverseChargeLineRequest: { note: note ?? null },
        }).unwrap();
        return { success: true as const, data: result };
      } catch (error) {
        console.error('Failed to reverse charge line:', error);
        return { success: false as const, error };
      }
    },
    [reverseMutation],
  );

  return { reverseChargeLine, isLoading, error };
}

// ============================================================================
// Claim Scrubber UI Hooks
// ============================================================================

/**
 * Hook for managing ClaimScrubber UI state
 * This hook only manages UI state - server state is handled by RTK Query
 */
/**
 * Smoothly scrolls an element to the center of the viewport
 *
 * @param element - The HTML element to scroll into view
 * @returns Promise that resolves when scrolling completes
 *
 * @remarks
 * Uses requestAnimationFrame to efficiently detect scroll completion by monitoring
 * when the scroll position stabilizes (movement < 0.5px for 3+ consecutive frames).
 * If no scroll movement is detected within 400ms (element already visible or scroll
 * failed), resolves immediately. Otherwise, lets the scroll complete naturally.
 */
const smoothScrollToCenter = (element: HTMLElement): Promise<void> => {
  return new Promise(resolve => {
    // Get the scrollable container (root-main) or fall back to window
    const scrollContainer = document.getElementById('root-main');

    // Early exit if element doesn't exist
    if (!element) {
      resolve();
      return;
    }

    let lastPosition = -1;
    let animationId: number | null = null;
    let samePositionCount = 0;
    let hasStartedMoving = false;
    let noMovementTimeout: NodeJS.Timeout | null = null;

    const getScrollPosition = (): number => {
      return scrollContainer ? scrollContainer.scrollTop : window.scrollY;
    };

    const cleanup = () => {
      if (animationId !== null) {
        cancelAnimationFrame(animationId);
        animationId = null;
      }
      if (noMovementTimeout !== null) {
        clearTimeout(noMovementTimeout);
        noMovementTimeout = null;
      }
    };

    const checkScroll = () => {
      const currentPosition = getScrollPosition();
      const delta = Math.abs(currentPosition - lastPosition);

      // Check if scroll has started moving
      if (!hasStartedMoving && currentPosition !== lastPosition) {
        hasStartedMoving = true;
        // Cancel the no-movement timeout since scroll is working
        if (noMovementTimeout !== null) {
          clearTimeout(noMovementTimeout);
          noMovementTimeout = null;
        }
      }

      // Only check for completion after scroll has started
      if (hasStartedMoving) {
        // Check if position is barely changing (< 0.5px movement)
        // This handles sub-pixel movements during easing
        if (delta < 0.5) {
          samePositionCount++;
          
          // Position stable for 3+ frames (~50ms) means scroll completed
          // This provides a small buffer to ensure easing has fully completed
          if (samePositionCount >= 3) {
            cleanup();
            resolve();
            return;
          }
        } else {
          // Position changed significantly, reset counter
          samePositionCount = 0;
        }
      }

      lastPosition = currentPosition;

      // Continue monitoring
      animationId = requestAnimationFrame(checkScroll);
    };

    // Set up timeout for case where scroll never starts
    // (element already visible or scroll fails)
    noMovementTimeout = setTimeout(() => {
      if (!hasStartedMoving) {
        cleanup();
        resolve();
      }
    }, 400);

    // Store initial position
    lastPosition = getScrollPosition();

    // Trigger smooth scroll
    try {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest',
      });
    } catch {
      // Fallback if scrollIntoView fails
      cleanup();
      resolve();
      return;
    }

    // Start monitoring scroll position
    animationId = requestAnimationFrame(checkScroll);
  });
};

export function useClaimScrubberUI() {
  const dispatch = useAppDispatch();
  const panel = useAppSelector(selectScrubberPanel);
  const navigation = useAppSelector(selectScrubberNavigation);
  const preferences = useAppSelector(selectScrubberPreferences);
  const activeBoxElementRef = useRef<HTMLElement | null>(null);

  // Panel actions
  const handleTogglePanel = useCallback(() => {
    dispatch(togglePanel());
  }, [dispatch]);

  const handleOpenPanel = useCallback(() => {
    dispatch(openPanel());
  }, [dispatch]);

  const handleClosePanel = useCallback(() => {
    dispatch(closePanel());
  }, [dispatch]);

  const handleSetPosition = useCallback(
    (position: 'bottom' | 'sidebar') => {
      dispatch(setPanelPosition(position));
    },
    [dispatch],
  );

  const handleToggleCollapse = useCallback(() => {
    dispatch(togglePanelCollapse());
  }, [dispatch]);

  const handleSetPanelWidth = useCallback(
    (width: number) => {
      dispatch(setPanelWidth(width));
    },
    [dispatch],
  );

  // Navigation actions
  const handleNavigateToField = useCallback(
    (field: string, box?: string | null) => {
      dispatch(navigateToField({ field, box }));

      // Clean up any previous highlight on box element
      if (activeBoxElementRef.current) {
        activeBoxElementRef.current.classList.remove('highlighted-box');
        activeBoxElementRef.current = null;
      }

      const fieldElement = document.querySelector(`[name="${field}"]`) as HTMLElement;
      
      // Determine which box to highlight
      let boxElement: HTMLElement | null = null;
      const boxNumber = box || navigation.highlightedBox;
      
      if (boxNumber) {
        // Try to find box by explicit box number
        boxElement = document.querySelector<HTMLElement>(`[data-cms-box="${boxNumber}"]`);
      }
      
      // If no box found yet and we have a field element, traverse DOM to find parent box
      if (!boxElement && fieldElement) {
        let parent: HTMLElement | null = fieldElement.parentElement;
        while (parent) {
          if (parent.hasAttribute('data-cms-box')) {
            boxElement = parent;
            break;
          }
          parent = parent.parentElement;
        }
      }
      
      const targetElement = boxElement || fieldElement;

      if (!targetElement || !fieldElement) return;

      // Store reference for cleanup (only box elements get highlighted)
      if (boxElement) {
        activeBoxElementRef.current = boxElement;
      }

      // Always scroll to center the element, then highlight after completion
      smoothScrollToCenter(targetElement).then(() => {
        // Scroll animation complete, now apply highlight and focus
        fieldElement.focus();
        if (boxElement) {
          boxElement.classList.add('highlighted-box');
        }
      });

      // Remove highlight using CSS animation end event (only for box elements)
      if (boxElement) {
        boxElement.addEventListener(
          'animationend',
          function handleAnimationEnd(e) {
            if (e.animationName === 'highlight-pulse') {
              boxElement.classList.remove('highlighted-box');
              dispatch(clearHighlight());
              if (activeBoxElementRef.current === boxElement) {
                activeBoxElementRef.current = null;
              }
            }
          },
          { once: true },
        );
      }
    },
    [dispatch, navigation.highlightedBox],
  );

  const handleClearHighlight = useCallback(() => {
    dispatch(clearHighlight());
  }, [dispatch]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (activeBoxElementRef.current) {
        activeBoxElementRef.current.classList.remove('highlighted-box');
      }
    };
  }, []);

  return {
    // State
    panel,
    navigation,
    preferences,

    // Actions
    togglePanel: handleTogglePanel,
    openPanel: handleOpenPanel,
    closePanel: handleClosePanel,
    setPosition: handleSetPosition,
    toggleCollapse: handleToggleCollapse,
    setPanelWidth: handleSetPanelWidth,
    togglePanelCollapse: handleToggleCollapse,
    navigateToField: handleNavigateToField,
    clearHighlight: handleClearHighlight,
  };
}

/**
 * Combined hook for ClaimScrubber functionality
 * Combines UI state management with RTK Query scrubbing
 */
export function useClaimScrubberWithUI() {
  const ui = useClaimScrubberUI();
  const [performScrub, { isLoading: isScrubbing }] = useClaimScrubMutation();

  const scrubClaim = useCallback(
    async (claimId: string, scrubData?: ClaimScrubRequest) => {
      try {
        const result = await performScrub({
          claimId,
          claimScrubRequest: scrubData || {},
        }).unwrap();

        // Always open panel if there are any annotations
        if (result?.annotations?.length) {
          ui.openPanel();
        }

        return { success: true, data: result };
      } catch (error) {
        console.error('Failed to scrub claim:', error);
        return { success: false, error };
      }
    },
    [performScrub, ui],
  );

  return {
    ...ui,
    scrubClaim,
    isScrubbing,
  };
}

// ============================================================================
// X12 Download Hooks
// ============================================================================

/**
 * Helper to trigger a browser file download from a blob result.
 */
function triggerBlobDownload({ blob, filename }: { blob: Blob; filename: string }) {
  const url = createBlobUrl(blob);
  triggerDownload(url, filename);
  // Revoke after a short delay to ensure download starts
  setTimeout(() => revokeBlobUrl(url), 100);
}

/**
 * Hook for downloading a single ERA (835) file.
 */
export function useEraDownload() {
  const [trigger, { isFetching }] = useLazyDownloadRemittanceEraQuery();
  const { enqueueSnackbar } = useSnackbar();
  const { billing } = useEventTracking();

  const downloadEra = useCallback(
    async (remittanceId: string) => {
      try {
        const result = await trigger({ remittanceId }).unwrap();
        triggerBlobDownload(result);
        billing.eraDownloaded(remittanceId, { source: 'detail_dialog' });
      } catch {
        billing.eraDownloadFailed(remittanceId, { source: 'detail_dialog' });
        enqueueSnackbar('Failed to download ERA file', { variant: 'error' });
      }
    },
    [trigger, enqueueSnackbar, billing],
  );

  return { downloadEra, isDownloading: isFetching };
}

/**
 * Hook for downloading multiple ERA (835) files as a ZIP.
 */
export function useBulkEraDownload() {
  const [trigger, { isLoading }] = useDownloadRemittanceErasBulkMutation();
  const { enqueueSnackbar } = useSnackbar();
  const { billing } = useEventTracking();

  const downloadBulkEra = useCallback(
    async (remittanceIds: string[]) => {
      try {
        const result = await trigger({ remittanceIds }).unwrap();
        triggerBlobDownload(result);
        billing.eraBulkDownloaded({ count: remittanceIds.length });
      } catch {
        billing.eraBulkDownloadFailed({ count: remittanceIds.length });
        enqueueSnackbar('Failed to download ERA files', { variant: 'error' });
      }
    },
    [trigger, enqueueSnackbar, billing],
  );

  return { downloadBulkEra, isDownloading: isLoading };
}

/**
 * Hook for downloading a single Claim X12 (837) file.
 */
export function useClaimX12Download() {
  const [trigger, { isFetching }] = useLazyDownloadClaimX12Query();
  const { enqueueSnackbar } = useSnackbar();
  const { billing } = useEventTracking();

  const downloadClaim = useCallback(
    async (claimId: string) => {
      try {
        const result = await trigger({ claimId }).unwrap();
        triggerBlobDownload(result);
        billing.claimX12Downloaded(claimId);
      } catch {
        billing.claimX12DownloadFailed(claimId);
        enqueueSnackbar('Failed to download claim file', { variant: 'error' });
      }
    },
    [trigger, enqueueSnackbar, billing],
  );

  return { downloadClaim, isDownloading: isFetching };
}

/**
 * Hook for downloading multiple Claim X12 (837) files as a ZIP.
 */
export function useBulkClaimX12Download() {
  const [trigger, { isLoading }] = useDownloadClaimsX12BulkMutation();
  const { enqueueSnackbar } = useSnackbar();
  const { billing } = useEventTracking();

  const downloadBulkClaims = useCallback(
    async (claimIds: string[]) => {
      try {
        const result = await trigger({ claimIds }).unwrap();
        triggerBlobDownload(result);
        billing.claimX12BulkDownloaded({ count: claimIds.length });
      } catch {
        billing.claimX12BulkDownloadFailed({ count: claimIds.length });
        enqueueSnackbar('Failed to download claim files', { variant: 'error' });
      }
    },
    [trigger, enqueueSnackbar, billing],
  );

  return { downloadBulkClaims, isDownloading: isLoading };
}
